import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BASE_URL } from "../services/config";
import { Card, CardBody, Spinner, Button } from "@heroui/react";
import api from "../services/api";
import { saveTokens, saveUserData, User } from "../services/auth.service";

export const YandexCallbackPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState<"loading" | "error">("loading");
  const [message, setMessage] = useState<string>(
    "Авторизуем вас через Яндекс..."
  );
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) {
      console.log("[YandexCallback] effect already executed, skipping");
      return;
    }
    hasRunRef.current = true;

    const run = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        const state = params.get("state");

        if (!code || !state) {
          setStatus("error");
          setMessage(
            "От Яндекса пришёл некорректный ответ. Попробуйте авторизоваться ещё раз."
          );
          return;
        }

        console.log("[YandexCallback] sending complete request", { code, state });

        // Шаг 1 — проверяем данные, полученные от Яндекс ID
        setCurrentStep(1);

        const resp = await api.post("/users/auth/yandex/complete/", {
          code,
          state,
        });

        // Шаг 2 — успешно вошли в аккаунт SAFONOVA
        setCurrentStep(2);

        const { access, refresh, user } = resp.data as {
          access: string;
          refresh?: string;
          user: User;
        };

        if (!access || !user) {
          setStatus("error");
          setMessage(
            "Не удалось завершить авторизацию через Яндекс. Попробуйте ещё раз."
          );
          return;
        }

        saveTokens(access, refresh);
        saveUserData(user);

        // Небольшая задержка перед редиректом, чтобы пользователь увидел третий шаг
        setCurrentStep(3);
        setTimeout(() => {
          window.location.href = "/account";
        }, 400);
      } catch (e: any) {
        console.error("Yandex auth complete error", e);
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          setMessage(
            "Ссылка авторизации истекла или недействительна. Попробуйте войти через Яндекс ещё раз."
          );
        } else {
          setMessage(
            "Авторизация через Яндекс не удалась — попробуйте ещё раз."
          );
        }
        setStatus("error");
      }
    };

    run();
  }, [location.search, history]);

  return (
    <section className="py-24 bg-background">
      <Helmet>
        <title>Авторизация через Яндекс — SAFONOVA</title>
        <meta
          name="description"
          content="Завершение авторизации через Яндекс ID для входа в личный кабинет SAFONOVA."
        />
        <link
          rel="canonical"
          href={`${BASE_URL || ""}${location.pathname}${location.search}`}
        />
      </Helmet>
      <div className="container mx-auto px-6">
        <Card className="max-w-md mx-auto border border-divider">
          <CardBody className="py-10 px-6 flex flex-col items-center text-center gap-6">
            {status === "loading" ? (
              <>
                <Spinner color="primary" />
                <div className="space-y-2 mt-4">
                  <p className="text-foreground font-medium">
                    Авторизуем вас через Яндекс...
                  </p>
                  <p className="text-foreground/70 text-sm">
                    Это обычно занимает не более 5–7 секунд. Пожалуйста, не закрывайте страницу.
                  </p>
                </div>

                <div className="mt-4 w-full text-left space-y-3 max-w-sm">
                  <div
                    className={`flex items-start gap-3 transition-opacity duration-300 ${
                      currentStep >= 1 ? "opacity-100" : "opacity-60"
                    }`}
                  >
                    <div
                      className={`mt-1 flex h-4 w-4 items-center justify-center rounded-full border border-primary/40 text-[10px] transition-colors duration-300 ${
                        currentStep >= 1 ? "bg-primary text-background" : "bg-transparent text-primary/60"
                      }`}
                    >
                      {currentStep >= 1 ? "✓" : "1"}
                    </div>
                    <p className="text-sm text-foreground/80">
                      Проверяем данные, полученные от Яндекс ID
                    </p>
                  </div>

                  <div
                    className={`flex items-start gap-3 transition-opacity duration-300 delay-75 ${
                      currentStep >= 2 ? "opacity-100" : "opacity-60"
                    }`}
                  >
                    <div
                      className={`mt-1 flex h-4 w-4 items-center justify-center rounded-full border border-primary/40 text-[10px] transition-colors duration-300 ${
                        currentStep >= 2 ? "bg-primary text-background" : "bg-transparent text-primary/60"
                      }`}
                    >
                      {currentStep >= 2 ? "✓" : "2"}
                    </div>
                    <p className="text-sm text-foreground/80">
                      Входим в ваш аккаунт
                    </p>
                  </div>

                  <div
                    className={`flex items-start gap-3 transition-opacity duration-300 delay-150 ${
                      currentStep >= 3 ? "opacity-100" : "opacity-60"
                    }`}
                  >
                    <div
                      className={`mt-1 flex h-4 w-4 items-center justify-center rounded-full border border-primary/40 text-[10px] transition-colors duration-300 ${
                        currentStep >= 3 ? "bg-primary text-background" : "bg-transparent text-primary/60"
                      }`}
                    >
                      {currentStep >= 3 ? "✓" : "3"}
                    </div>
                    <p className="text-sm text-foreground/80">
                      Перенаправляем вас в личный кабинет
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-foreground/80 mb-4">{message}</p>
                <Button
                  color="primary"
                  variant="flat"
                  onPress={() => history.push("/auth")}
                >
                  Вернуться к форме входа
                </Button>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </section>
  );
};

export default YandexCallbackPage;
